/**
 * Non-blocking status strip for proctoring and connectivity.
 *
 * v1 policy, stated here because this is where a future reader will look for
 * it: these banners INFORM. Nothing in this component ends an exam, blocks
 * input, or hides the paper. A false positive — a notification stealing focus,
 * a Wi-Fi hiccup — must cost the student nothing.
 *
 * The one banner with an action is the fullscreen one, and its action is a
 * button the student presses themselves: browsers only grant fullscreen from a
 * user gesture, so it cannot be re-entered automatically.
 */
import {
  AlertTriangle,
  Maximize2,
  ShieldAlert,
  Smartphone,
  WifiOff,
} from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";

function Strip({
  tone,
  icon,
  children,
  action,
}: {
  tone: "warning" | "violation";
  icon: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex items-center gap-2 border-b px-4 py-1.5",
        tone === "violation"
          ? "border-violation/30 bg-violation-soft"
          : "border-review/30 bg-review-soft",
      )}
    >
      <span
        className={cn(
          "shrink-0",
          tone === "violation" ? "text-violation" : "text-review",
        )}
      >
        {icon}
      </span>
      <p className="min-w-0 flex-1 text-2xs text-ink">{children}</p>
      {action}
    </div>
  );
}

export function ProctorBanner({
  fullscreenActive,
  online,
  phoneAlive,
  violations,
  saveDegraded,
  pendingUploads,
  onReturnToFullscreen,
}: {
  fullscreenActive: boolean;
  online: boolean;
  phoneAlive: boolean;
  violations: number;
  saveDegraded: boolean;
  pendingUploads: number;
  onReturnToFullscreen: () => void;
}) {
  return (
    <div className="flex flex-col">
      {!fullscreenActive ? (
        <Strip
          tone="violation"
          icon={<Maximize2 className="size-3.5" />}
          action={
            <Button size="sm" variant="primary" onClick={onReturnToFullscreen}>
              Return to fullscreen
            </Button>
          }
        >
          You have left fullscreen. This has been logged. Your exam is still
          running and nothing has been lost.
        </Strip>
      ) : null}

      {!online ? (
        <Strip tone="warning" icon={<WifiOff className="size-3.5" />}>
          You are offline. Keep working — your answers are saved on this device
          and will be sent as soon as the connection returns.
        </Strip>
      ) : null}

      {!phoneAlive ? (
        <Strip tone="warning" icon={<Smartphone className="size-3.5" />}>
          The phone camera is not sending video. Check that the phone is awake
          and the page is open. Your exam continues either way.
        </Strip>
      ) : null}

      {saveDegraded ? (
        <Strip tone="warning" icon={<AlertTriangle className="size-3.5" />}>
          Could not save your last answer. Retrying automatically — do not
          refresh the page.
        </Strip>
      ) : null}

      {violations > 0 ? (
        <Strip tone="warning" icon={<ShieldAlert className="size-3.5" />}>
          <span className="tabular font-medium">{violations}</span>{" "}
          {violations === 1 ? "activity has" : "activities have"} been recorded
          for review. This does not affect your marks or end your exam.
          {pendingUploads > 0 ? (
            <span className="text-ink-muted">
              {" "}
              · <span className="tabular">{pendingUploads}</span> video segments
              waiting to upload
            </span>
          ) : null}
        </Strip>
      ) : null}
    </div>
  );
}
